import { useEffect, useState } from "react";
import { Search, Star, Heart, ChevronRight, Filter, X, Mic2, Play, Bot, Paperclip } from "lucide-react";
import { http, fmtRupee, errMsg } from "../api";
import AudioPlayer from "../components/AudioPlayer";

function HireModal({ voice, onClose, onDone }) {
  const [form, setForm] = useState({ project_title: "", description: "", instructions: "", delivery_format: "MP3", estimated_hours: 1, deadline: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [script, setScript] = useState(null);
  const [reference, setReference] = useState(null);
  const submit = async e => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await http.post("/orders", { ...form, sample_id: voice.id, deadline: new Date(form.deadline).toISOString() });
      for (const [kind, f] of [["script", script], ["reference", reference]]) {
        if (!f) continue;
        const fd = new FormData(); fd.append("kind", kind); fd.append("file", f);
        await http.post(`/orders/${data.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onDone("Order request sent. Artist has 12 hours to acknowledge.");
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 16);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="hire-modal">
        <div className="modal-head">
          <div>
            <h2>Hire {voice.public_id}</h2>
            <p style={{ fontSize: 13, marginTop: 4 }}>{voice.language} · {voice.category} · {fmtRupee(voice.marketplace_rate)} / finished hour</p>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <div>
            <label>Project title</label>
            <input data-testid="hire-title" required onChange={e => setForm({ ...form, project_title: e.target.value })} placeholder="Audiobook chapter 1 narration" />
          </div>
          <div>
            <label>Project description</label>
            <textarea data-testid="hire-description" required rows={3} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Tone, target audience, references..." />
          </div>
          <div className="field-row">
            <div>
              <label>Estimated finished hours</label>
              <input data-testid="hire-hours" type="number" step="0.25" min="0.25" required onChange={e => setForm({ ...form, estimated_hours: parseFloat(e.target.value) })} />
            </div>
            <div>
              <label>Deadline</label>
              <input data-testid="hire-deadline" type="datetime-local" required min={minDate} onChange={e => setForm({ ...form, deadline: e.target.value })} />
            </div>
          </div>
          <div className="field-row">
            <div>
              <label>Delivery format</label>
              <select onChange={e => setForm({ ...form, delivery_format: e.target.value })}>
                <option>MP3</option><option>WAV</option><option>Both</option>
              </select>
            </div>
            <div>
              <label>Voice direction / notes (optional)</label>
              <input onChange={e => setForm({ ...form, instructions: e.target.value })} placeholder="Pronunciation, pace..." />
            </div>
          </div>
          <div className="field-row">
            <div>
              <label><Paperclip size={12} style={{ display: "inline" }} /> Script (PDF / DOCX / TXT, optional)</label>
              <label className="dropzone" style={{ padding: 10 }}><input type="file" accept=".pdf,.doc,.docx,.txt,application/pdf" onChange={e => setScript(e.target.files[0])} data-testid="hire-script-file" /><p style={{ fontSize: 12.5 }}>{script ? script.name : "Attach script to be recorded"}</p></label>
            </div>
            <div>
              <label>Reference audio (how you want it, optional)</label>
              <label className="dropzone" style={{ padding: 10 }}><input type="file" accept="audio/*" onChange={e => setReference(e.target.files[0])} data-testid="hire-reference-file" /><p style={{ fontSize: 12.5 }}>{reference ? reference.name : "Attach a sample you like"}</p></label>
            </div>
          </div>
          <div style={{ padding: 12, background: "var(--surface-2)", borderRadius: 8, fontSize: 13 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: "var(--muted)" }}>Rate</span><strong>{fmtRupee(voice.marketplace_rate)} / hour</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--muted)" }}>Estimated total</span>
              <strong>{fmtRupee((form.estimated_hours || 0) * voice.marketplace_rate)}</strong>
            </div>
          </div>
          {err && <div className="error">{err}</div>}
          <button className="primary wide" type="submit" disabled={busy} data-testid="hire-submit">
            {busy ? "Sending..." : "Send hiring request"} <ChevronRight size={15} />
          </button>
        </form>
      </div>
    </div>
  );
}

function VoiceCard({ v, onHire, favorites, toggleFav, guest }) {
  const isFav = favorites.includes(v.id);
  return (
    <article className={`voice-card ${v.featured ? "featured" : ""}`} data-testid={`voice-card-${v.id}`}>
      <div className="voice-top">
        <div className="voice-id">
          <div className="voice-avatar"><Mic2 size={18} /></div>
          <div>
            <strong>{v.public_id}</strong>
            <small>{v.gender} · {v.voice_age}</small>
          </div>
        </div>
        {!guest && (
          <button data-testid={`shortlist-${v.id}`} className={`star-btn ${isFav ? "active" : ""}`} onClick={() => toggleFav(v.id)}>
            <Heart size={16} fill={isFav ? "currentColor" : "none"} />
          </button>
        )}
      </div>
      <AudioPlayer id={v.id} compact />
      <div className="voice-meta">
        <span>{v.language}</span>{(v.categories || [v.category]).slice(0, 2).map(c => <span key={c}>{c}</span>)}
        {v.ai_voice && <span className="ai-tag" data-testid={`ai-voice-${v.id}`}><Bot size={11} /> AI voice</span>}
      </div>
      <div className="tag-row">{v.characteristics?.slice(0, 4).map(c => <span className="badge" key={c}>{c}</span>)}</div>
      <div className="voice-bottom">
        <div className="voice-price">
          <strong>{fmtRupee(v.marketplace_rate)}</strong>
          <small>per finished hour</small>
        </div>
        <div className="voice-rating">
          <Star size={13} fill="currentColor" />
          {v.rating > 0 ? v.rating.toFixed(1) : "New"}
          <small>({v.review_count || 0})</small>
        </div>
      </div>
      <button className="primary wide" onClick={() => onHire(v)} data-testid={`hire-voice-${v.id}`}>
        Hire this voice <ChevronRight size={15} />
      </button>
    </article>
  );
}

export default function ClientMarketplace({ user }) {
  const [config, setConfig] = useState({ languages: [], categories: [], characteristics: [], voice_ages: [] });
  const [filters, setFilters] = useState({ language: "", category: "Audiobook / Narration", gender: "", voice_age: "", characteristic: "", min_price: "", max_price: "", sort: "recommended", search: "" });
  const [voices, setVoices] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [hiring, setHiring] = useState(null);
  const [notice, setNotice] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => { http.get("/config").then(r => setConfig(r.data)); }, []);
  useEffect(() => {
    if (user?.role === "client") http.get("/favorites").then(r => setFavorites(r.data.map(v => v.id))).catch(() => {});
  }, [user]);
  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    http.get("/marketplace", { params }).then(r => setVoices(r.data));
  }, [filters]);

  const toggleFav = async id => {
    try {
      const { data } = await http.post(`/favorites/${id}`);
      setFavorites(data.favorited ? [...favorites, id] : favorites.filter(x => x !== id));
    } catch (e) { setNotice(errMsg(e)); }
  };
  const startHire = v => { if (!user || user.role !== "client") { setNotice("Sign in as a client to hire a voice."); return; } setHiring(v); };
  const clearFilters = () => setFilters({ language: "", category: "", gender: "", voice_age: "", characteristic: "", min_price: "", max_price: "", sort: "recommended", search: "" });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Voices with a point of view</h1>
          <p>Listen first. Hire the right sound for your story.</p>
        </div>
        <div className="page-head-side">
          <span className="badge primary">{voices.length} approved voices</span>
        </div>
      </div>

      <div className="filters">
        <div className="filter-search">
          <Search size={17} color="var(--muted)" />
          <input data-testid="marketplace-search-input" placeholder="Search voice ID or title..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} />
        </div>
        <select data-testid="language-filter" value={filters.language} onChange={e => setFilters({ ...filters, language: e.target.value })}>
          <option value="">All languages</option>
          {config.languages.map(x => <option key={x}>{x}</option>)}
        </select>
        <select data-testid="category-filter" value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All use cases</option>
          {config.categories.map(x => <option key={x}>{x}</option>)}
        </select>
        <select data-testid="sort-filter" value={filters.sort} onChange={e => setFilters({ ...filters, sort: e.target.value })}>
          <option value="recommended">Recommended</option>
          <option value="top-rated">Top rated</option>
          <option value="most-reviewed">Most reviewed</option>
          <option value="newest">Newest</option>
          <option value="price-low">Price: low to high</option>
          <option value="price-high">Price: high to low</option>
        </select>
      </div>

      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <button className="ghost" onClick={() => setShowAdvanced(v => !v)} data-testid="toggle-advanced-filters">
          <Filter size={14} /> {showAdvanced ? "Hide" : "More"} filters
        </button>
        <button className="ghost" onClick={clearFilters} data-testid="clear-filters">Clear all</button>
      </div>

      {showAdvanced && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="filter-advanced">
            <select data-testid="gender-filter" value={filters.gender} onChange={e => setFilters({ ...filters, gender: e.target.value })}>
              <option value="">Any gender</option>
              <option>Male</option><option>Female</option><option>Non-binary</option>
            </select>
            <select data-testid="voice-age-filter" value={filters.voice_age} onChange={e => setFilters({ ...filters, voice_age: e.target.value })}>
              <option value="">Any voice age</option>
              {config.voice_ages.map(a => <option key={a}>{a}</option>)}
            </select>
            <input data-testid="min-price-filter" type="number" placeholder="Min ₹/hour" value={filters.min_price} onChange={e => setFilters({ ...filters, min_price: e.target.value })} />
            <input data-testid="max-price-filter" type="number" placeholder="Max ₹/hour" value={filters.max_price} onChange={e => setFilters({ ...filters, max_price: e.target.value })} />
          </div>
          <div className="section-title" style={{ marginTop: 8 }}>VOICE CHARACTERISTICS</div>
          <div className="chip-row">
            <button className={`chip ${!filters.characteristic ? "active" : ""}`} onClick={() => setFilters({ ...filters, characteristic: "" })}>Any</button>
            {config.characteristics.map(c => (
              <button key={c} className={`chip ${filters.characteristic === c ? "active" : ""}`} onClick={() => setFilters({ ...filters, characteristic: c })} data-testid={`char-chip-${c}`}>{c}</button>
            ))}
          </div>
        </div>
      )}

      {notice && <div className="notice success" data-testid="workflow-notice">{notice}</div>}

      <div className="section-title"><span>CURATED FOR YOUR BRIEF</span><span>{voices.length} results</span></div>
      <div className="voice-grid">
        {voices.map(v => <VoiceCard key={v.id} v={v} onHire={startHire} favorites={favorites} toggleFav={toggleFav} guest={!user || user.role !== "client"} />)}
      </div>
      {voices.length === 0 && (
        <div className="empty" data-testid="marketplace-empty">
          <Mic2 size={32} />
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No voices match these filters yet</div>
          <div style={{ fontSize: 13 }}>Try clearing filters or picking a different language.</div>
        </div>
      )}

      {hiring && <HireModal voice={hiring} onClose={() => setHiring(null)} onDone={msg => { setHiring(null); setNotice(msg); }} />}
    </>
  );
}
